import { Box, Typography, Grid, makeStyles } from "@material-ui/core";

import { Icon } from "../base";
import { ShaDowBox } from "../boxes";
import { NoDataCard } from "../parts";
import { COLORS } from "../../theme/colors";
import OwnerImg from "../../assets/images/icons/owner.svg";
import TenantImg from "../../assets/images/icons/tenant.svg";
import { capitalize, getLabel, pluralize } from "../../services";
import DoubleCircleImg from "../../assets/images/icons/double-circle.svg";
import SubMetersImg from "../../assets/images/icons/central-with-sub.svg";
import PublicImg from "../../assets/images/icons/sub-meter-and-public.svg";
import { METER_PER_UNIT } from "../../services/constants";

const useStyles = makeStyles(() => ({
  rightSide: {
    borderRight: `1px solid ${COLORS.darks[0][25]}`,
    paddingBottom: 20,
  },
}));

const ownedBy = {
  TENANT: "owned by tenant",
  PMC: "owned by PMC",
  BUILDING_OWNER: "owned by building owner",
};

const ownerImg = {
  TENANT: TenantImg,
  PMC: PublicImg,
  BUILDING_OWNER: OwnerImg,
};

const NoMeterData = ({ title }) => (
  <Box mt={6}>
    <Typography style={{ fontWeight: 700, textAlign: "center", color: COLORS.darks[0][25] }}>
      {title}
    </Typography>
    <Typography
      style={{
        fontWeight: 700,
        fontSize: 21,
        textAlign: "center",
        color: COLORS.darks[0][25],
      }}
    >
      N/A
    </Typography>
  </Box>
);

const MeterInfo = ({ meterPlan }) => {
  const allAreNull = [
    meterPlan?.unit_meters,
    meterPlan?.public_meters,
    meterPlan?.unit_meters_owner,
    meterPlan?.public_meters_owner,
  ].every((item) => item === null);

  if (allAreNull) return <NoMeterData title="Unit/Public breakdown" />;

  if (
    (meterPlan?.unit_meters || meterPlan?.public_meters) &&
    !meterPlan?.unit_meters_owner &&
    !meterPlan?.public_meters_owner
  )
    return (
      <Box mt={6}>
        {meterPlan.unit_meters ? (
          <Typography style={{ fontWeight: 700, marginBottom: 20 }}>
            {meterPlan.unit_meters} unit{" "}
            {pluralize(meterPlan.unit_meters, "meter", { withCount: false })}
          </Typography>
        ) : null}
        {meterPlan.public_meters ? (
          <Typography style={{ fontWeight: 700 }}>
            {meterPlan.public_meters} public{" "}
            {pluralize(meterPlan.public_meters, "meter", { withCount: false })}
          </Typography>
        ) : null}
      </Box>
    );

  if (
    meterPlan?.unit_meters &&
    meterPlan?.public_meters &&
    meterPlan?.unit_meters_owner &&
    meterPlan?.public_meters_owner
  )
    return (
      <Box style={{ paddingLeft: 16 }}>
        <Grid
          container
          alignItems="center"
          justifyContent="center"
          wrap="nowrap"
          style={{ marginBottom: 36, marginTop: 16 }}
        >
          <Icon
            src={ownerImg[meterPlan?.unit_meters_owner]}
            style={{
              padding: meterPlan?.unit_meters_owner === "TENANT" ? "0 7px" : 0,
              width: meterPlan?.unit_meters_owner !== "TENANT" ? 50 : "auto",
            }}
          />
          <Typography component="span" style={{ marginLeft: 16 }}>
            <Typography component="span" style={{ fontWeight: 700 }}>
              {meterPlan.unit_meters} unit{" "}
              {pluralize(meterPlan.unit_meters, "meter", { withCount: false })}{" "}
            </Typography>
            {ownedBy[meterPlan?.unit_meters_owner || ""]}
          </Typography>
        </Grid>
        <Grid container alignItems="center" justifyContent="center" wrap="nowrap">
          <Icon
            src={ownerImg[meterPlan?.public_meters_owner]}
            style={{
              padding: meterPlan?.public_meters_owner === "TENANT" ? "0 7px" : 0,
              width: meterPlan?.public_meters_owner !== "TENANT" ? 50 : "auto",
            }}
          />
          <Typography component="span" style={{ marginLeft: 16 }}>
            <Typography component="span" style={{ fontWeight: 700 }}>
              {meterPlan.public_meters} public{" "}
              {pluralize(meterPlan.public_meters, "meter", { withCount: false })}{" "}
            </Typography>
            {ownedBy[meterPlan?.public_meters_owner || ""]}
          </Typography>
        </Grid>
      </Box>
    );

  return (
    <Box>
      <Grid container justifyContent="center" alignItems="center" style={{ marginTop: 10 }}>
        <Icon
          src={ownerImg[meterPlan?.unit_meters_owner || meterPlan?.public_meters_owner]}
          style={{
            marginBottom: 30,
            ...ownerImg[
              meterPlan?.unit_meters_owner
                ? meterPlan?.unit_meters_owner
                : meterPlan.public_meters_owner
            ].style,
          }}
        />
      </Grid>
      <Typography style={{ fontWeight: 700, textAlign: "center", padding: "0 10px" }}>
        {meterPlan?.unit_meters ? (
          <Typography component="span">
            <Typography component="span" style={{ fontWeight: 700 }}>
              {meterPlan.unit_meters} unit{" "}
              {pluralize(meterPlan.unit_meters, "meter", { withCount: false })}{" "}
            </Typography>
            {ownedBy[meterPlan?.unit_meters_owner || ""]}
          </Typography>
        ) : null}
        {meterPlan?.unit_meters && meterPlan?.public_meters ? (
          <Typography component="span">{" + "}</Typography>
        ) : null}
        {meterPlan?.public_meters ? (
          <Typography component="span">
            <Typography component="span" style={{ fontWeight: 700 }}>
              {meterPlan.public_meters} public{" "}
              {pluralize(meterPlan.public_meters, "meter", { withCount: false })}{" "}
            </Typography>
            {ownedBy[meterPlan?.public_meters_owner || ""]}
          </Typography>
        ) : null}
      </Typography>
    </Box>
  );
};

const title = {
  CENTRAL: "Central meter, no sub-meters",
  SUBMETERS: "Central meter, with sub-meters",
  METER_PER_UNIT: "Meter per unit",
};

export const MeterPlan = ({ meterPlan }) => {
  const classes = useStyles();

  if (!meterPlan) return <NoDataCard title={title[meterPlan?.type || METER_PER_UNIT]} />;

  if (meterPlan.type)
    return (
      <ShaDowBox fullHeight>
        <Box style={{ padding: 24 }}>
          <Typography variant="h4">{title[meterPlan?.type || METER_PER_UNIT]}</Typography>
          {meterPlan?.type ? (
            <Grid container style={{ marginTop: 50 }}>
              <Grid item sm={6} className={classes.rightSide}>
                {meterPlan?.type === "CENTRAL" ? (
                  <Icon
                    src={DoubleCircleImg}
                    style={{
                      display: "block",
                      width: 100,
                      height: 100,
                      margin: "0 auto",
                      marginBottom: 30,
                    }}
                  />
                ) : meterPlan?.type === "SUBMETERS" ? (
                  <Icon
                    src={SubMetersImg}
                    style={{
                      display: "block",
                      margin: "0 auto 30px",
                    }}
                  />
                ) : (
                  <Grid container style={{ width: 102, margin: "0 auto 30px" }}>
                    <Grid item xs={6} style={{ marginBottom: 10 }}>
                      <Icon src={DoubleCircleImg} />
                    </Grid>
                    <Grid item xs={6} style={{ textAlign: "center" }}>
                      <Icon src={DoubleCircleImg} />
                    </Grid>
                    <Grid item xs={6}>
                      <Icon src={DoubleCircleImg} />
                    </Grid>
                    <Grid item xs={6} style={{ textAlign: "center" }}>
                      <Icon src={DoubleCircleImg} />
                    </Grid>
                  </Grid>
                )}
                {meterPlan?.total_meters ? (
                  meterPlan?.type === "CENTRAL" ? (
                    <>
                      <Typography variant="h2" style={{ textAlign: "center" }}>
                        {meterPlan?.total_meters}
                      </Typography>
                      <Typography style={{ textAlign: "center" }}>
                        {pluralize(meterPlan?.total_meters, "meter", { withCount: false })}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography style={{ textAlign: "center", fontWeight: 700 }}>
                        Total{" "}
                        <Typography component="span" variant="h2">
                          {meterPlan?.total_meters}
                        </Typography>
                      </Typography>
                      <Typography style={{ textAlign: "center" }}>
                        {pluralize(
                          meterPlan?.total_meters,
                          meterPlan?.type === "SUBMETERS" ? "Sub-Meter" : "Meter",
                          { withCount: false }
                        )}
                      </Typography>
                    </>
                  )
                ) : null}
              </Grid>
              <Grid container item justifyContent="center" sm={6} style={{ paddingBottom: 20 }}>
                <MeterInfo meterPlan={meterPlan} />
              </Grid>
            </Grid>
          ) : null}
        </Box>
      </ShaDowBox>
    );

  return (
    <ShaDowBox fullHeight>
      <Box style={{ padding: 24 }}>
        <Typography variant="h4">{title[meterPlan?.type || METER_PER_UNIT]}</Typography>
        {!meterPlan?.type && (
          <Grid container style={{ marginTop: 50 }}>
            <Grid item sm={6} className={classes.rightSide}>
              <Grid container style={{ width: 102, margin: "0 auto 30px" }}>
                <Grid item xs={6} style={{ marginBottom: 10 }}>
                  <Icon src={DoubleCircleImg} />
                </Grid>
                <Grid item xs={6} style={{ textAlign: "center" }}>
                  <Icon src={DoubleCircleImg} />
                </Grid>
                <Grid item xs={6}>
                  <Icon src={DoubleCircleImg} />
                </Grid>
                <Grid item xs={6} style={{ textAlign: "center" }}>
                  <Icon src={DoubleCircleImg} />
                </Grid>
              </Grid>
              <Grid container justifyContent="center" alignItems="baseline">
                <Typography style={{ marginRight: 5 }}>Total</Typography>
                <Typography variant="h2">{meterPlan?.total_meters}</Typography>
              </Grid>
              <Typography style={{ textAlign: "center" }}>
                {pluralize(meterPlan?.total_meters, "Meter", { withCount: false })}
              </Typography>
            </Grid>
            <Grid container item sm={6} justifyContent="center" style={{ paddingBottom: 20 }}>
              {meterPlan?.meters_owner ? (
                <>
                  <Grid container justifyContent="center" style={{ marginTop: 16 }}>
                    <Icon
                      src={ownerImg[meterPlan?.meters_owner]}
                      style={{
                        marginBottom: 30,
                      }}
                    />
                  </Grid>
                  <Typography style={{ maxWidth: 135, textAlign: "center" }}>
                    Owned by {capitalize(getLabel(meterPlan?.meters_owner || ""))}
                  </Typography>
                </>
              ) : (
                <NoMeterData title="Unit breakdown" />
              )}
            </Grid>
          </Grid>
        )}
      </Box>
    </ShaDowBox>
  );
};
